/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { Command, NodeAuthProvider } from './';
import { Flow } from '@store/async';
import { Document, Match, MessageType, OpenAIFunction, Timer, ToolHumanApproval } from '@common/hooks/useChatbot';
import { LoadTabProperties } from '@common/hooks/useWorkflow';
import { FullEdge } from '@components/Flow';
import { v4 as uuidv4 } from 'uuid';
import { NodeField, NodeFieldType, NodeInputType, NodeRequestParameter } from '@components/Flow/Nodes';
import { filterJumpFields } from '@components/Flow/Nodes/Logic/JumpTo/Tabs';
import { State } from '..';
import { MessagingChatMessage } from '@components/Flow/Nodes/Trigger/InboundMessage/MessageClient/message.types';
import {
  MessageParameters,
  MessageParameterType,
  NodeTraceEvent,
  RunWorkflowParameters,
  runWorkflowThunk,
  WorkflowChat,
} from '@store/async/run';

import {
  Connection,
  Edge,
  EdgeChange,
  NodeChange,
  Viewport,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  updateEdge,
} from 'reactflow';

export enum NodeTraceStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
}

export type NodeTrace = {
  type: string;
  icon: string;
  name: string;
  description: any | null;
  showCollapse: boolean;
  background?: string;
  collapsed: boolean;
  metadata?: {
    node_id: string;
    workflow_id: string;
    status: string;
    timers: Timer[];
    duration: number | null;
    output?: any;
    webhooks: string[];
    cancelled_webhooks: string[];
    jump_increment?: number;
    max_jumps?: number;
  };
};

type TraceEventBoolean = { caught: boolean; triggered: boolean };

type TraceEvents = {
  openChat: TraceEventBoolean;
  openTools: TraceEventBoolean;
  openCall: TraceEventBoolean;
  endCall: TraceEventBoolean;
  openParentTab: TraceEventBoolean;
  openMessaging: TraceEventBoolean;
};

export type WorkflowTrace = {
  id: string | null;
  workflow_id: string | null;
  error: any | null;
  running: boolean;
  status: { [node_id: string]: string };
  events: TraceEvents;
  edges: Edge[];
  traces: NodeTrace[];
  messages: MessageType[];
  messagingChat: MessagingChatMessage[];
  nodeErrors: { node_id: string; error: string }[];
  savedParameters: { [key: string]: NodeRequestParameter<string> };
  savedMessagingParameters: { [key: string]: MessageParameterType };
  chatNodes: Command[];
  chat: WorkflowChat[];
  abortController: AbortController | null;
  aborted: boolean;
  parameters: RunWorkflowParameters | null;
};

type NodeErrorMetadata = {
  // the list of input parameters passed to the node
  parameters?: { name: string; value: string }[];
};

type NodeParentError = {
  statusCode: number;
  message: string;
};

type NodeErrorField = {
  id: string;
  message: string;
  // defined if the error field is not in the first tab
  tab?: string;
};

type NodeError = {
  id: string; // the node id
  name: string; // the name of the node
  error: NodeParentError | null; // adds an error message to the top of the node sidebar
  fields: NodeErrorField[]; // adds an error to each field in the node sidebar defined in this array
  metadata: NodeErrorMetadata | null; // used to store additional error info
  workflowId: string;
};

export interface Tab {
  flow_id: string;

  // the name of the tab
  label: string;

  // the current selected node
  selectedNode: string | null;

  // Indexing nodes and edges
  nodeIndex: number;
  edgeIndex: number;

  flowError: string | null;
  loading: boolean;

  // config for determing the path for executing the flow
  service_path: string;

  // true when user is dragging an edge from a handle
  connecting: boolean;

  // true when the flow has no unsaved changes
  saved: boolean;

  // flow, nodes and edges
  flow: Flow | null;
  edges: Edge[];
  commands: Command[];

  // stores the current position of the viewport and zoom level
  viewport: Viewport;

  // determines which edges are visible on the graph
  show_execution: boolean;
  show_tools: boolean;
  show_data: boolean;

  // true if the flow has never been saved
  new: boolean;

  // represents if the flow has even been saved in the database
  unstored: boolean;
  duplicate: boolean;

  messages: MessageType[];
  nodesLoading: string[];
  error: string | null;

  // used to handle state for executing the workflow
  run: WorkflowTrace;

  // a list of active errors with nodes in the workflow
  nodeErrors: NodeError[];

  // the workflow data for the modnodes inside the parent workflow
  modules: Tab[];

  // store the current state of edges before selecting to reset afterwards
  edgesCache: Edge[];

  // the currently selected edge
  selectedEdge: Edge | null;
}

export type tabTypes = {
  list: Tab[];
  last_menu: string | null;
  active: Tab;
};

const defaultRun: WorkflowTrace = {
  id: null,
  workflow_id: null,
  error: null,
  traces: [],
  status: {},
  running: false,
  events: {
    openChat: { caught: false, triggered: false },
    openTools: { caught: false, triggered: false },
    openCall: { caught: false, triggered: false },
    endCall: { caught: false, triggered: false },
    openParentTab: { caught: false, triggered: false },
    openMessaging: { caught: false, triggered: false },
  },
  edges: [],
  messages: [],
  messagingChat: [],
  nodeErrors: [],
  savedParameters: {},
  savedMessagingParameters: {},
  chatNodes: [],
  chat: [],
  aborted: false,
  parameters: null,
  abortController: null,
};

export const defaultActiveTab: Tab = {
  flow_id: '',
  label: '',
  selectedNode: null,
  nodeIndex: 0,
  edgeIndex: 0,
  flowError: null,
  loading: false,
  service_path: '',
  connecting: false,
  saved: true,
  flow: null,
  edges: [],
  commands: [],
  new: false,
  viewport: {
    x: 0,
    y: 0,
    zoom: 1,
  },
  show_execution: true,
  show_tools: true,
  show_data: true,
  nodesLoading: [],
  duplicate: false,
  unstored: false,
  messages: [],
  error: null,
  run: defaultRun,
  nodeErrors: [],
  modules: [],
  edgesCache: [],
  selectedEdge: null,
};

const initialState: tabTypes = {
  list: [],
  last_menu: null,
  active: defaultActiveTab,
};

// Selector to fetch the commands array
const selectCommands = (state: State) => state.tabs.active.commands;
const selectTraces = (state: State) => state.tabs.active.run.traces || [];

// Selector to fetch the selected command by node ID
export const selectCommandByNodeId = createSelector(
  [selectCommands, (_, nodeId: string | null) => nodeId],
  (commands, nodeId) => commands.find((command) => command.node.id === nodeId)
);

export const selectTracesByNodeId = createSelector(
  [selectTraces, (state: any) => state.tabs.active.flow?.flow_id, (_: any, nodeId: string | undefined) => nodeId],
  (traces, workflowId, nodeId) =>
    traces.find((trace) => trace.metadata?.workflow_id === workflowId && trace.metadata?.node_id === nodeId)
);

const tabSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    clear: (state) => {
      state.active = defaultActiveTab;
      state.list = [];
      state.last_menu = null;
    },
    reset: (state) => {
      state.active = defaultActiveTab;
    },
    setCurrent: (state, action: PayloadAction<string | null>) => {
      state.active = state.list.find((tab) => tab.flow_id === action.payload) || state.active;
      state.active.loading = false;
    },
    createTab: (state, action: PayloadAction<LoadTabProperties>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === action.payload.flow_id) tab = { ...tab, ...action.payload, loading: false };
        return tab;
      });

      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    updateTabModules: (state, action: PayloadAction<{ moduleId: string; tab: LoadTabProperties }>) => {
      state.list = state.list.map((tab) => {
        if (tab.commands.some((command) => command?.state?.moduleId === action.payload.moduleId)) {
          if (tab.modules.some((mod) => mod.flow_id === action.payload.moduleId)) {
            const refreshedModules = tab.modules.map((mod) => {
              if (mod.flow_id === action.payload.moduleId) {
                return action.payload.tab;
              }

              return mod;
            });

            tab = { ...tab, modules: refreshedModules };
          } else {
            tab = { ...tab, modules: tab.modules.concat(action.payload.tab) };
          }
        }

        return tab;
      });

      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    clearWorkflowNodeError: (state, action: PayloadAction<string>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== action.payload);
        }

        return tab;
      });

      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setWorkflowRunLoading: (
      state,
      action: PayloadAction<{
        running: boolean;
        parameters: RunWorkflowParameters;
        chatNodes: Command[];
      }>
    ) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.running = action.payload.running;
          tab.run.parameters = action.payload.parameters;
          tab.run.nodeErrors = [];
          tab.run.chatNodes = action.payload.chatNodes;
          tab.run.traces = [];
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    startWorkflowRun: (state, action: PayloadAction<{ run_id: string; workflow_id: string }>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.id = action.payload.run_id;
          tab.run.workflow_id = action.payload.workflow_id;
          tab.nodeErrors = [];
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setAbortController: (state, action: PayloadAction<AbortController | null>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.abortController = action.payload;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    updateTraceNodeDuration: (
      state,
      action: PayloadAction<{ node_id: string; workflow_id: string; duration: number }>
    ) => {
      const node_id = action.payload.node_id;
      const workflow_id = action.payload.workflow_id;
      const duration = action.payload.duration;

      // update the execution duration of a node
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.traces = tab.run.traces.map((trace) => {
            if (trace?.metadata?.node_id === node_id && trace?.metadata?.workflow_id === workflow_id) {
              return { ...trace, metadata: { ...trace.metadata, duration } };
            }

            return trace;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    updateTraceEvent: (
      state,
      action: PayloadAction<{ event: keyof TraceEvents; field: 'triggered' | 'caught'; value: boolean }>
    ) => {
      const event = action.payload.event;
      const field = action.payload.field;
      const value = action.payload.value;

      // trace events are used to trigger code outside of the async thunk while streaming events inside it
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          return {
            ...tab,
            run: {
              ...tab.run,
              events: {
                ...tab.run.events,
                [event]: {
                  ...tab.run.events[event],
                  [field]: value,
                },
              },
            },
          };
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addNodeError: (state, action: PayloadAction<NodeError>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.nodeErrors = tab.nodeErrors
            .filter(
              (nodeError) => !(nodeError.id === action.payload.id && nodeError.workflowId === action.payload.workflowId)
            )
            .concat(action.payload);
        }

        const nodeId = action.payload.id;
        const workflowId = action.payload.workflowId;

        // uncollapsed the node to show error
        tab.run.traces = tab.run.traces.map((trace) => {
          if (trace?.metadata?.node_id === nodeId && trace?.metadata?.workflow_id === workflowId)
            return { ...trace, collapsed: false };

          return trace;
        });

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addDataPrefix: (state, action: PayloadAction<{ node_id: string; prefix: string }>) => {
      const node_id = action.payload.node_id;
      const prefix = action.payload.prefix;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id && tab.flow) {
          tab.flow.data_prefixes = {
            ...tab.flow.data_prefixes,
            [node_id]: prefix,
          };
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    deleteDataPrefix: (state, action: PayloadAction<{ node_id: string }>) => {
      const node_id = action.payload.node_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id && tab.flow) {
          if (tab.flow.data_prefixes?.[node_id]) delete tab.flow.data_prefixes[node_id];
          tab.saved = false;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removeNodeErrors: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.nodeErrors = tab.nodeErrors.filter((err) => err.id !== nodeId);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removeNodeFieldErrors: (state, action: PayloadAction<{ nodeId: string; fieldId: string }>) => {
      const { nodeId, fieldId } = action.payload;
      const removeErrors: string[] = [];

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.nodeErrors = tab.nodeErrors.map((nodeError) => {
            if (nodeError.id === nodeId) {
              const newFields = nodeError?.fields?.filter((nodeField) => nodeField.id !== fieldId);
              if (newFields.length === 0) removeErrors.push(nodeError.id);

              return {
                ...nodeError,
                fields: newFields,
              };
            }

            return nodeError;
          });
        }

        return tab;
      });

      // removes the error if there are no field errors left
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.nodeErrors = tab.nodeErrors.filter((nodeError) => !removeErrors.includes(nodeError.id));
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addJumpTraceIncrement: (
      state,
      action: PayloadAction<{
        node_id: string | null;
        workflow_id: string | null;
        increment: number;
        max_jumps: number;
        target_node_id: string;
      }>
    ) => {
      const workflow_id = action.payload.workflow_id;
      const node_id = action.payload.node_id;
      const increment = action.payload.increment;
      const max_jumps = action.payload.max_jumps;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.traces = tab.run.traces.map((trace) => {
            if (trace?.metadata?.node_id === node_id && trace?.metadata?.workflow_id === workflow_id) {
              return {
                ...trace,
                metadata: {
                  ...trace.metadata,
                  max_jumps,
                  jump_increment: increment,
                },
              };
            }

            return trace;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addTrace: (
      state,
      action: PayloadAction<{ node_id: string | null; workflow_id: string | null; trace: NodeTrace }>
    ) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          if (
            tab.run.traces.some(
              (trace) =>
                trace?.metadata?.node_id === action.payload.node_id &&
                trace?.metadata?.workflow_id === action.payload.workflow_id
            )
          ) {
            tab.run.traces = tab.run.traces.map((trace) => {
              if (
                trace?.metadata?.node_id === action.payload.node_id &&
                trace?.metadata?.workflow_id === action.payload.workflow_id
              ) {
                return {
                  ...trace,
                  metadata: {
                    ...trace.metadata,
                    status: action?.payload?.trace?.metadata?.status || '',
                  },
                };
              }

              return trace;
            });
          } else {
            tab.run.traces = tab.run.traces.concat(action.payload.trace);
          }
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removeTrace: (state, action: PayloadAction<{ node_id: string | null; workflow_id: string | null }>) => {
      const workflow_id = action.payload.workflow_id;
      const node_id = action.payload.node_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.traces = tab.run.traces.filter(
            (trace) => !(trace?.metadata?.node_id === node_id && trace?.metadata?.workflow_id === workflow_id)
          );

          const statusId = `${node_id}/${workflow_id}`;
          if (tab.run.status.hasOwnProperty(statusId)) delete tab.run.status[statusId];
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setTraceCollapse: (state, action: PayloadAction<{ node_id: string; workflow_id: string; collapsed: boolean }>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.traces = tab.run.traces.map((trace) => {
            if (
              trace?.metadata?.node_id === action.payload.node_id &&
              trace?.metadata?.workflow_id === action.payload.workflow_id
            ) {
              return {
                ...trace,
                collapsed: action.payload.collapsed,
              };
            }

            return trace;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addTraceWebhook: (
      state,
      action: PayloadAction<{ node_id: string; workflow_id: string; type: NodeTraceEvent; webhook: string }>
    ) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.traces = tab.run.traces.map((trace) => {
            if (
              trace?.metadata?.node_id === action.payload.node_id &&
              trace?.metadata?.workflow_id === action.payload.workflow_id
            ) {
              if (action.payload.type === NodeTraceEvent.WebhookPending) {
                return {
                  ...trace,
                  metadata: {
                    ...trace.metadata,
                    webhooks: trace.metadata?.webhooks.concat(action.payload.webhook),
                    cancelled_webhooks: [],
                  },
                };
              }

              if (
                action.payload.type === NodeTraceEvent.WebhookReceived ||
                action.payload.type === NodeTraceEvent.WebhookCancelled
              ) {
                return {
                  ...trace,
                  metadata: {
                    ...trace.metadata,
                    webhooks: trace.metadata?.webhooks.filter((webhook) => webhook !== action.payload.webhook),
                    cancelled_webhooks:
                      action.payload.type === NodeTraceEvent.WebhookCancelled
                        ? trace.metadata.cancelled_webhooks.concat(action.payload.webhook)
                        : trace.metadata.cancelled_webhooks,
                  },
                };
              }
            }

            return trace;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addSavedParameter: (state, action: PayloadAction<{ id: string; value: NodeRequestParameter<string> }>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.savedParameters[action.payload.id] = action.payload.value;
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    deleteSavedParameter: (state, action: PayloadAction<string>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          delete tab.run.savedParameters[action.payload];
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addSavedMessagingParameter: (state, action: PayloadAction<{ field: keyof MessageParameters; value: any }>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.savedMessagingParameters[action.payload.field] = action.payload.value;
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    deleteSavedMessagingParameter: (state, action: PayloadAction<keyof MessageParameters>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          delete tab.run.savedMessagingParameters[action.payload];
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessage: (
      state,
      action: PayloadAction<{ id: string; node_id: string; workflow_id: string; bot_id: string; message: string }>
    ) => {
      const { id, message, node_id, workflow_id, bot_id } = action.payload;

      const userMessage = {
        id: id,
        type: 'user',
        node_id,
        workflow_id,
        message: message,
        stopped: false,
        events: {
          classification: { action: null, input: null },
          functions: [],
          timers: [],
          documents: [],
          matches: [],
          toolApproval: null,
        },
      };

      const botResponse = {
        id: bot_id,
        source_id: id,
        node_id,
        workflow_id,
        type: 'bot',
        message: '',
        source: message,
        state: 'loading',
        show_feedback: false,
        show_help_action: false,
        events: {
          classification: { action: null, input: null },
          functions: [],
          timers: [],
          documents: [],
          matches: [],
          toolApproval: null,
        },
      };

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.concat(userMessage, botResponse);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatLoadMessages: (
      state,
      action: PayloadAction<{ node_id: string; messages: { id: string; role: string; content: string }[] }>
    ) => {
      const { node_id, messages } = action.payload;

      const messageArray: any[] = messages.map((message) => {
        if (message.role === 'user') {
          return {
            id: uuidv4(),
            type: 'user',
            node_id,
            message: message.content,
            stopped: false,
            events: {
              classification: { action: null, input: null },
              functions: [],
              timers: [],
              documents: [],
              matches: [],
            },
          };
        }

        return {
          id: uuidv4(),
          source_id: uuidv4(),
          node_id,
          type: 'bot',
          message: message.content,
          source: uuidv4(),
          state: 'complete',
          show_feedback: false,
          show_help_action: false,
          events: {
            classification: { action: null, input: null },
            functions: [],
            timers: [],
            documents: [],
            matches: [],
          },
        };
      });

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = messageArray;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatUpdateMessage: (state, action: PayloadAction<{ id: string; field: string; value: string }>) => {
      const id = action.payload.id;
      const field = action.payload.field;
      const value = action.payload.value;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id) return { ...item, [field]: value };
            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddWorkflowChat: (state, action: PayloadAction<WorkflowChat>) => {
      const chat = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          if (!tab.run.chat.some((item) => item.node_id === chat.node_id && item.workflow_id === chat.workflow_id))
            tab.run.chat.push(chat);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageError: (state, action: PayloadAction<{ id: string; value: { detail: string } }>) => {
      const id = action.payload.id;
      const error = action.payload.value;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id) return { ...item, message: error.detail, state: 'error' };
            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageDocuments: (state, action: PayloadAction<{ id: string; value: Document[] }>) => {
      const id = action.payload.id;
      const documents = action.payload.value;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            const new_documents = [...item.events.documents, ...documents].filter(
              (v, i, a) => a.findIndex((v2) => v2.url === v.url) === i
            );

            if (item.id === id) return { ...item, events: { ...item.events, documents: new_documents } };

            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageMatches: (state, action: PayloadAction<{ id: string; value: Match[] }>) => {
      const id = action.payload.id;
      const matches = action.payload.value;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            const new_matches = [...item.events.matches, ...matches].filter(
              (v, i, a) => a.findIndex((v2) => v2.url === v.url) === i
            );

            if (item.id === id) return { ...item, events: { ...item.events, matches: new_matches } };
            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageToolSelected: (
      state,
      action: PayloadAction<{ id: string; node_id: string; workflow_id: string; value: OpenAIFunction }>
    ) => {
      const id = action.payload.id;
      const tool = action.payload.value;
      const workflow_id = action.payload.workflow_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id && item.workflow_id === workflow_id)
              return { ...item, events: { ...item.events, functions: [...item.events.functions, tool] } };
            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageUpdateTool: (
      state,
      action: PayloadAction<{ id: string; node_id: string; workflow_id: string; value: OpenAIFunction }>
    ) => {
      const id = action.payload.id;
      const tool = action.payload.value;
      const workflow_id = action.payload.workflow_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id && item.workflow_id === workflow_id)
              return {
                ...item,
                events: {
                  ...item.events,
                  functions: item.events.functions.map((func) => {
                    if (func.id === tool.id) return tool;
                    return func;
                  }),
                },
              };

            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageAddToolApproval: (
      state,
      action: PayloadAction<{ id: string; node_id: string; workflow_id: string; value: ToolHumanApproval }>
    ) => {
      const id = action.payload.id;
      const toolApproval = action.payload.value;
      const workflow_id = action.payload.workflow_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id && item.workflow_id === workflow_id)
              return {
                ...item,
                events: {
                  ...item.events,
                  toolApproval,
                },
              };

            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageApproveToolApproval: (
      state,
      action: PayloadAction<{ id: string; node_id: string; workflow_id: string }>
    ) => {
      const id = action.payload.id;
      const workflow_id = action.payload.workflow_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id && item.workflow_id === workflow_id)
              return {
                ...item,
                events: {
                  ...item.events,
                  toolApproval: item.events.toolApproval
                    ? {
                        ...item.events.toolApproval,
                        approved: true,
                      }
                    : null,
                },
              };

            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageRemoveToolApproval: (
      state,
      action: PayloadAction<{ id: string; node_id: string; workflow_id: string }>
    ) => {
      const id = action.payload.id;
      const workflow_id = action.payload.workflow_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id && item.workflow_id === workflow_id)
              return {
                ...item,
                events: {
                  ...item.events,
                  toolApproval: null,
                },
              };

            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageRetryTool: (state, action: PayloadAction<{ id: string; value: OpenAIFunction }>) => {
      const id = action.payload.id;
      const tool = action.payload.value;
      const workflow_id = action.payload.value?.workflow_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id && item.workflow_id === workflow_id)
              return {
                ...item,
                events: {
                  ...item.events,
                  functions: item.events.functions.filter((func) => func.id !== tool.id),
                },
              };

            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageTool: (state, action: PayloadAction<{ id: string; value: OpenAIFunction }>) => {
      const id = action.payload.id;
      const tool = action.payload.value;
      const workflow_id = action.payload.value?.workflow_id;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id && item.workflow_id === workflow_id)
              return { ...item, events: { ...item.events, functions: [...item.events.functions, tool] } };
            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    chatAddMessageToken: (state, action: PayloadAction<{ id: string; value: string }>) => {
      const id = action.payload.id;
      const token = action.payload.value;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messages = tab.run.messages.map((item) => {
            if (item.id === id) return { ...item, message: item.message + token, state: 'streaming' };
            return item;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    messagingAddMessage: (state, action: PayloadAction<MessagingChatMessage>) => {
      const message = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.messagingChat = tab.run.messagingChat.concat(message);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    errorWorkflowRun: (state, action: PayloadAction<string>) => {
      const error = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.running = false;
          tab.run.error = error;
          if (tab.run.edges.length > 0) tab.edges = tab.run.edges;

          // error running nodes
          for (const key of Object.keys(tab.run.status)) {
            if (tab.run.status[key] === 'processing') tab.run.status[key] = 'failed';
          }

          tab.run.traces = tab.run.traces.map((trace) => {
            if (trace.metadata?.status === 'processing') {
              return {
                ...trace,
                metadata: {
                  ...trace.metadata,
                  status: 'pending',
                },
              };
            }

            return trace;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    errorWorkflowRunNode: (state, action: PayloadAction<{ node_id: string; error: string }>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.running = false;
          tab.run.nodeErrors.push(action.payload);
          tab.run.error = action.payload.error;

          for (const key of Object.keys(tab.run.status)) {
            if (tab.run.status[key] === 'processing') tab.run.status[key] = 'pending';
          }

          tab.run.traces = tab.run.traces.map((trace) => {
            if (trace.metadata?.status === 'processing') {
              return {
                ...trace,
                metadata: {
                  ...trace.metadata,
                  status: 'pending',
                },
              };
            }

            return trace;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    stopWorkflowRun: (state) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          if (!tab.run?.id && tab.edges.length > 0) tab.edges = tab.run.edges;
          tab.run.running = false;

          for (const key of Object.keys(tab.run.status)) {
            if (tab.run.status[key] === 'processing') tab.run.status[key] = 'pending';
          }

          tab.run.traces = tab.run.traces.map((trace) => {
            if (trace.metadata?.status === 'processing') {
              return {
                ...trace,
                metadata: {
                  ...trace.metadata,
                  status: 'pending',
                },
              };
            }

            return trace;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    cancelWorkflowRun: (state) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.running = false;
          tab.run.aborted = true;

          for (const key of Object.keys(tab.run.status)) {
            if (tab.run.status[key] === 'processing') tab.run.status[key] = 'pending';
          }

          tab.run.traces = tab.run.traces.map((trace) => {
            if (trace.metadata?.status === 'processing') {
              return {
                ...trace,
                metadata: {
                  ...trace.metadata,
                  status: 'pending',
                },
              };
            }

            return trace;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setNodeRunStatus: (state, action: PayloadAction<{ node_id: string; workflow_id: string; status: string }>) => {
      const { node_id, workflow_id, status } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.run.status[`${node_id}/${workflow_id}`] = status;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    clearWorkflowRun: (state) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          if (tab.run.edges.length > 0) tab.edges = tab.run.edges;
          tab.run = {
            ...defaultRun,
            savedParameters: tab.run.savedParameters,
            savedMessagingParameters: tab.run.savedMessagingParameters,
            nodeErrors: tab.run.nodeErrors,
          };

          tab.nodeErrors = tab.nodeErrors.filter((nodeError) => nodeError?.fields?.length > 0);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    createLoadingTab: (
      state,
      action: PayloadAction<{ workflow_id: string; label: string | string; viewport: Viewport }>
    ) => {
      const newTab = {
        ...defaultActiveTab,
        flow_id: action.payload.workflow_id,
        label: action.payload.label || action.payload.workflow_id,
        service_path: action.payload.workflow_id,
        loading: true,
        new: false,
        unstored: false,
        viewport: action.payload.viewport,
      };

      state.list = state.list.concat(newTab);
      state.active = newTab;
    },
    createNewTab: (state, action: PayloadAction<string>) => {
      const newTab = {
        ...defaultActiveTab,
        flow_id: action.payload,
        label: '',
        flow: {
          flow_id: action.payload,
          service_id: '',
          user_id: '',
          org_id: '',
          service_path: '',
          name: '',
          node_count: 0,
          trigger_type: '',
          created_by: '',
          updated_by: '',
          created_at: '',
          updated_at: '',
          public: false,
          authentication: {
            OPENAI_API_KEY: null,
            GOOGLE_API_KEY: null,
            ANTHROPIC_API_KEY: null,
            GROK_API_KEY: null,
            GROQ_API_KEY: null,
          },
          data_prefixes: {},
        },
        new: true,
        unstored: true,
        service_path: action.payload,
      };
      state.list = state.list.concat(newTab);
      state.active = newTab;
    },
    completeTabCreation: (state) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) tab.new = false;
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setNodeLoading: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) tab.nodesLoading = tab.nodesLoading.concat(nodeId);
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    stopNodeLoading: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) tab.nodesLoading = tab.nodesLoading.filter((id) => id !== nodeId);
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    duplicate: (state, action: PayloadAction<{ tab_flow_id: string; id: string }>) => {
      const { tab_flow_id, id } = action.payload;

      const tabToDuplicate = state.list.find((tab) => tab.flow_id === tab_flow_id);
      if (!tabToDuplicate) return;

      const newTab = {
        ...tabToDuplicate,
        flow_id: id,
        flow: tabToDuplicate?.flow ? { ...tabToDuplicate?.flow, flow_id: id } : null,
        label: `${tabToDuplicate.label} (copy)`,
        service_path: id,
        saved: false,
        unstored: true,
        new: true,
        duplicate: true,
      };

      state.list = state.list.concat(newTab);
      state.active = newTab;
    },
    refreshExecutionEdgeColor: (state, action: PayloadAction<{ color: string; wrongColor: string }>) => {
      const color = action.payload.color;
      const wrongColor = action.payload.wrongColor;

      for (const tab of state.list) {
        tab.edges = tab.edges.map((edge: any) => {
          if (edge?.style?.stroke === wrongColor) {
            return {
              ...edge,
              style: {
                ...edge.style,
                stroke: color,
              },
            };
          }

          return edge;
        });
      }

      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    changeEdgeColor: (state, action: PayloadAction<{ color: string; existingColor: string }>) => {
      const existingColor = action.payload.existingColor;
      const color = action.payload.color;

      for (const tab of state.list) {
        tab.edges = tab.edges.map((edge: any) => {
          if (edge?.style?.stroke === existingColor) {
            return {
              ...edge,
              style: {
                ...edge.style,
                stroke: color,
              },
            };
          }

          return edge;
        });
      }

      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setNodeAuthProvider: (state, action: PayloadAction<{ key: keyof NodeAuthProvider; value: string | null }>) => {
      const { key, value } = action.payload;

      for (const tab of state.list) {
        if (tab.flow_id === state.active.flow_id && tab.flow) {
          tab.flow.authentication[key as keyof NodeAuthProvider] = value;
        }
      }

      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    cancelTabCreation: (state) => {
      state.list = state.list.filter((tab) => !tab.new);
      state.active = defaultActiveTab;
    },
    setLastMenu: (state, action: PayloadAction<string | null>) => {
      state.last_menu = action.payload;
    },
    removeTab: (state, action: PayloadAction<string>) => {
      state.list = state.list.filter((tab) => tab.flow_id !== action.payload);
      if (state.active.flow_id === action.payload) state.active = defaultActiveTab;
    },
    setIdentifier: (state, action: PayloadAction<string | null>) => {
      if (!action.payload) return;

      state.active.error = null;
      state.active.flow_id = action.payload;
      state.active = state.list.find((tab) => tab.flow_id === action.payload) || state.active;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.error = action.payload;
          tab.loading = false;
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setSelectedNode: (state, action: PayloadAction<string | null>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.selectedNode = action.payload;
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === action.payload) {
              return {
                ...command,
                node: {
                  ...command.node,
                  selected: true,
                },
              };
            }

            return { ...command, node: { ...command.node, selected: false } };
          });

          // remove selected edge
          if (tab.selectedEdge) {
            tab.selectedEdge = null;
            tab.edges = tab.edgesCache;
          }
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    prepareEdgesForRun: (state, action: PayloadAction<{ edges: Edge[]; stroke?: string }>) => {
      const stroke = action.payload.stroke || '#000000';

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          // reset edges if sending another message in the chat window
          if (tab.run.edges.length > 0) tab.edges = tab.run.edges;

          tab.run.status = {};
          tab.run.edges = action.payload.edges;

          tab.commands.map((command) => ({
            ...command,
            node: {
              ...command.node,
              selected: false,
            },
          }));

          tab.edges = tab.edges.map((edge) => {
            if (edge.animated) {
              return {
                ...edge,
                style: {
                  ...edge.style,
                  stroke,
                  strokeDasharray: '5,5',
                },
                animated: false,
              };
            }

            return {
              ...edge,
              style: {
                ...edge.style,
                stroke,
              },
            };
          });
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setEdgesColor: (state, action: PayloadAction<{ edgeIds: string[]; stroke: string }>) => {
      const edgeIds = action.payload.edgeIds;
      const stroke = action.payload.stroke;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.edges = tab.edges.map((edge) => {
            if (!edgeIds.includes(edge.id) || edge?.style?.stroke === stroke) return edge;

            if (edge.animated) {
              return {
                ...edge,
                style: {
                  ...edge.style,
                  stroke,
                  strokeDasharray: '5,5',
                },
                animated: false,
              };
            }

            return {
              ...edge,
              style: {
                ...edge.style,
                stroke,
              },
            };
          });
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    selectEdge: (state, action: PayloadAction<{ edges: Edge[]; selectedEdge: Edge; stroke: string }>) => {
      const stroke = action.payload.stroke || '#000000';

      state.list = state.list.map((tab) => {
        // if edge is already selected
        if (tab?.selectedEdge?.id === action.payload.selectedEdge.id) return tab;

        if (tab.flow_id === state.active.flow_id) {
          // reset the edges if an edge is already selected
          if (tab.selectedEdge) {
            tab.selectedEdge = null;
            tab.edges = tab.edgesCache;
          }

          // unselect a node if selected
          tab.selectedNode = null;

          // set the edge as selected
          tab.selectedEdge = action.payload.selectedEdge;

          tab.commands.map((command) => ({
            ...command,
            node: {
              ...command.node,
              selected: false,
            },
          }));

          tab.edges = tab.edges.map((edge) => {
            // dont update the selected edge
            if (edge.id === action.payload.selectedEdge.id) return edge;

            if (edge.animated) {
              return {
                ...edge,
                style: {
                  ...edge.style,
                  stroke,
                  strokeDasharray: '5,5',
                },
                animated: false,
              };
            }

            return {
              ...edge,
              style: {
                ...edge.style,
                stroke,
              },
            };
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setEdgeStyle: (state, action: PayloadAction<{ connection: Edge; field: string; value: string | boolean }>) => {
      const { connection, field, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.edges = tab.edges.map((edge) => {
            if (
              edge.source === connection.source &&
              edge.sourceHandle === connection.sourceHandle &&
              edge.target === connection.target &&
              edge.targetHandle === connection.targetHandle
            ) {
              return {
                ...edge,
                style: {
                  ...edge.style,
                  [field]: value,
                },
              };
            }

            return edge;
          });
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setEdgeAnimating: (state, action: PayloadAction<{ connection: Edge; value: boolean }>) => {
      const { connection, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.edges = tab.edges.map((edge) => {
            if (
              edge.source === connection.source &&
              edge.sourceHandle === connection.sourceHandle &&
              edge.target === connection.target &&
              edge.targetHandle === connection.targetHandle
            ) {
              return {
                ...edge,
                animated: value,
              };
            }

            return edge;
          });
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setConnecting: (state, action: PayloadAction<boolean>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) tab.connecting = action.payload;
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) tab.loading = action.payload;
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setTabName: (state, action: PayloadAction<string>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.label = action.payload;
          tab.saved = false;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setTabMessages: (state, action: PayloadAction<MessageType[]>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.messages = action.payload;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    // createLoadingTab: (state, action: PayloadAction<{ id: string | null; name: string | null }>) => {
    //   if (!action.payload.id || !action.payload.name) return;

    //   state.list = state.list.concat({
    //     ...defaultActiveTab,
    //     flow_id: action.payload.id,
    //     label: action.payload.name,
    //   });
    // },
    updateConnection: (
      state,
      action: PayloadAction<{ oldEdge: Edge; newConnection: Connection; updatedEdge: FullEdge }>
    ) => {
      const { oldEdge, newConnection, updatedEdge } = action.payload;
      const edge = updatedEdge.edge;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;
          tab.edges = updateEdge(oldEdge, newConnection, tab.edges);

          tab.commands = tab.commands.map((command) => {
            const node = command.node;

            // update node sources
            if (node.id === newConnection.target) {
              return {
                ...command,
                connections: [...command.connections, updatedEdge],
                node: {
                  ...node,
                  data: {
                    ...node.data,
                    sources: [...node.data.sources, edge],
                  },
                },
              };
            }

            // update node targets
            if (node.id === newConnection.source) {
              return {
                ...command,
                connections: [...command.connections, updatedEdge],
                node: {
                  ...node,
                  data: {
                    ...node.data,
                    targets: [...node.data.targets, edge],
                  },
                },
              };
            }

            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    onEdgeChange: (state, action: PayloadAction<EdgeChange[]>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;
          tab.edges = applyEdgeChanges(action.payload, tab.edges);
          if (!tab.selectedEdge) tab.edgesCache = tab.edges;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    onNodeChange: (state, action: PayloadAction<NodeChange[]>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          const nodes = applyNodeChanges(
            action.payload,
            tab.commands.map((command) => command.node)
          );

          if (action.payload.some((change) => 'positionAbsolute' in change || 'position' in change)) tab.saved = false;
          tab.commands = tab.commands.map((command) => {
            const node = nodes.find((item) => item.id === command.node.id);
            if (node?.id) return { ...command, node };
            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addCommand: (state, action: PayloadAction<{ command: Command | null; pair: Command | null; callId?: string }>) => {
      const command = action.payload.command;
      const pair = action.payload.pair;
      const callId = action.payload.callId;

      if (!command) return;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;
          tab.commands = tab.commands.map((command) => ({
            ...command,
            node: {
              ...command.node,
              selected: false,
            },
          }));

          const newCommand = !callId
            ? command
            : {
                ...command,
                state: { ...command.state, call_control_id: { type: NodeInputType.Expression, value: callId } },
              };

          if (!pair) {
            tab.nodeIndex = tab.nodeIndex + 1;
            tab.commands = [...tab.commands, newCommand];
          } else {
            tab.nodeIndex = tab.nodeIndex + 2;
            tab.commands = [...tab.commands, newCommand, pair];
          }

          tab.selectedNode = command.node.id;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    addEdge: (state, action: PayloadAction<FullEdge>) => {
      const edge = action.payload.edge;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;
          tab.edges = addEdge(edge, tab.edges);
          tab.edgeIndex = tab.edgeIndex + 1;

          tab.commands = tab.commands.map((command) => {
            const node = command.node;

            // update node sources
            if (node.id === edge.target) {
              return {
                ...command,
                connections: [...command.connections, action.payload],
                node: {
                  ...node,
                  data: {
                    ...node.data,
                    sources: [...node.data.sources, edge],
                  },
                },
              };
            }

            // update node targets
            if (node.id === edge.source) {
              return {
                ...command,
                connections: [...command.connections, action.payload],
                node: {
                  ...node,
                  data: {
                    ...node.data,
                    targets: [...node.data.targets, edge],
                  },
                },
              };
            }

            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removeEdgeConnections: (state, action: PayloadAction<{ node_id: string; handle: string }>) => {
      const node_id = action.payload.node_id;
      const handle = action.payload.handle;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;

          tab.commands = tab.commands.map((command: Command) => {
            const node = command.node;

            return {
              ...command,
              connections: command.connections.filter(
                (connection) =>
                  !(
                    (connection.edge.source === node_id && connection.edge.sourceHandle === handle) ||
                    (connection.edge.target === node_id && connection.edge.targetHandle === handle)
                  )
              ),
              node: {
                ...node,
                data: {
                  sources: node.data.sources.filter(
                    (item: Connection) =>
                      !(
                        (item.source === node_id && item.sourceHandle === handle) ||
                        (item.target === node_id && item.targetHandle === handle)
                      )
                  ),
                  targets: node.data.targets.filter(
                    (item: Connection) =>
                      !(
                        (item.source === node_id && item.sourceHandle === handle) ||
                        (item.target === node_id && item.targetHandle === handle)
                      )
                  ),
                },
              },
            };
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removeEdgeByHandle: (state, action: PayloadAction<{ node_id: string; handle: string }>) => {
      const node_id = action.payload.node_id;
      const handle = action.payload.handle;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;
          tab.edges = tab.edges.filter(
            (edge) =>
              !(
                (edge.source === node_id && edge.sourceHandle === handle) ||
                (edge.target === node_id && edge.targetHandle === handle)
              )
          );

          tab.edgesCache = tab.edgesCache.filter(
            (edge) =>
              !(
                (edge.source === node_id && edge.sourceHandle === handle) ||
                (edge.target === node_id && edge.targetHandle === handle)
              )
          );

          tab.commands = tab.commands.map((command: Command) => {
            const node = command.node;

            return {
              ...command,
              connections: command.connections.filter(
                (connection) =>
                  !(
                    (connection.edge.source === node_id && connection.edge.sourceHandle === handle) ||
                    (connection.edge.target === node_id && connection.edge.targetHandle === handle)
                  )
              ),
              node: {
                ...node,
                data: {
                  sources: node.data.sources.filter(
                    (item: Connection) =>
                      !(
                        (item.source === node_id && item.sourceHandle === handle) ||
                        (item.target === node_id && item.targetHandle === handle)
                      )
                  ),
                  targets: node.data.targets.filter(
                    (item: Connection) =>
                      !(
                        (item.source === node_id && item.sourceHandle === handle) ||
                        (item.target === node_id && item.targetHandle === handle)
                      )
                  ),
                },
              },
            };
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removeEdgeById: (state, action: PayloadAction<{ edgeId: string }>) => {
      const edgeId = action.payload.edgeId;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;
          tab.edges = tab.edges.filter((edge) => edge.id !== edgeId);
          tab.edgesCache = tab.edgesCache.filter((edge) => edge.id !== edgeId);

          tab.commands = tab.commands.map((command: Command) => {
            const node = command.node;

            return {
              ...command,
              connections: command.connections.filter((connection) => connection.edge.id !== edgeId),
              node: {
                ...node,
                data: {
                  sources: node.data.sources.filter((item: any) => item.id !== edgeId),
                  targets: node.data.targets.filter((item: any) => item.id !== edgeId),
                },
              },
            };
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    duplicateNode: (state, action: PayloadAction<string>) => {
      const node_id = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;

          const nodeToDuplicate = tab.commands.find((command) => command.node.id === node_id);
          if (!nodeToDuplicate) return tab;

          const duplicateNode = {
            ...nodeToDuplicate,
            node: {
              ...nodeToDuplicate.node,
              id: state.active.nodeIndex.toString(),
              position: {
                x: nodeToDuplicate.node.position.x,
                y: nodeToDuplicate.node.position.y + 250,
              },
              data: { targets: [], sources: [] },
            },
            connections: [],
          };

          tab.nodeIndex += 1;
          tab.commands = tab.commands.concat(duplicateNode);
          tab.selectedNode = state.active.nodeIndex.toString();

          return {
            ...tab,
            commands: tab.commands.map((command) => {
              if (command.node.id !== duplicateNode.node.id) {
                return {
                  ...command,
                  node: { ...command.node, selected: false },
                };
              }

              return command;
            }),
          };
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removeNode: (state, action: PayloadAction<string>) => {
      const node_id = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;
          tab.commands = tab.commands.map((command: Command) => {
            const node = command.node;

            return {
              ...command,
              connections: command.connections.filter(
                (connection) => connection.edge.source !== node_id && connection.edge.target !== node_id
              ),
              node: {
                ...node,
                data: {
                  sources: node.data.sources.filter(
                    (item: Connection) => item.source !== node_id && item.target !== node_id
                  ),
                  targets: node.data.targets.filter(
                    (item: Connection) => item.target !== node_id && item.source !== node_id
                  ),
                },
              },
            };
          });

          // unselect node if it is selected when deleted
          if (tab.selectedNode === node_id) tab.selectedNode = null;

          // remove the edges associated with the node
          tab.edges = tab.edges.filter((edge: any) => edge.source !== node_id && edge.target !== node_id);

          // remove the nodes
          tab.commands = tab.commands.filter((command: Command) => command.node.id !== node_id);

          // reset the JumpTo configurations
          tab.commands = tab.commands.map((command: any) => {
            if (command?.state?.node_id === node_id) {
              return {
                ...command,
                state: {
                  node_id: '',
                  max_jumps: { type: NodeInputType.Number, value: command?.state?.max_jumps?.value || '3' },
                },
                fields: command.fields.filter((field: NodeField) => field.id === 'node_id' || field.id === 'max_jumps'),
              };
            }

            return command;
          });

          // remove any data prefixes
          if (tab.flow?.data_prefixes.hasOwnProperty(node_id)) delete tab.flow.data_prefixes[node_id];
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removeEdges: (state, action: PayloadAction<string>) => {
      const node_id = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;
          tab.edges = tab.edges.filter((edge) => edge.source !== action.payload && edge.target !== action.payload);
          tab.edgesCache = tab.edgesCache.filter(
            (edge) => edge.source !== action.payload && edge.target !== action.payload
          );

          tab.commands = tab.commands.map((command: Command) => {
            const node = command.node;

            return {
              ...command,
              connections: command.connections.filter(
                (connection) => connection.edge.source !== node_id && connection.edge.target !== node_id
              ),
              node: {
                ...node,
                data: {
                  sources: node.data.sources.filter(
                    (item: Connection) => item.source !== node_id && item.target !== node_id
                  ),
                  targets: node.data.targets.filter(
                    (item: Connection) => item.target !== node_id && item.source !== node_id
                  ),
                },
              },
            };
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    removePluginConnections: (state, action: PayloadAction<string>) => {
      const node_id = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = false;

          tab.commands = tab.commands.map((command: Command) => {
            const node = command.node;

            const plugins = command.connections.filter(
              (connection) => connection.target_anchor.variant === 'plugin' && connection.edge.target === node_id
            );

            tab.edges = tab.edges.filter(
              (edge) =>
                !(
                  edge.target === node_id &&
                  plugins.some(
                    (plugin) =>
                      plugin.edge.sourceHandle === edge.sourceHandle && plugin.edge.targetHandle === edge.targetHandle
                  )
                )
            );

            return {
              ...command,
              connections: command.connections.filter(
                (connection) => !(connection.target_anchor.variant === 'plugin' && connection.edge.target == node_id)
              ),
              node: {
                ...node,
                data: {
                  sources: node.data.sources.filter(
                    (item: Connection) =>
                      !(
                        item.target === node_id &&
                        plugins.some(
                          (plugin) =>
                            plugin.edge.sourceHandle === item.sourceHandle &&
                            plugin.edge.targetHandle === item.targetHandle
                        )
                      )
                  ),
                  targets: node.data.targets.filter(
                    (item: Connection) =>
                      !(
                        item.target === node_id &&
                        plugins.some(
                          (plugin) =>
                            plugin.edge.sourceHandle === item.sourceHandle &&
                            plugin.edge.targetHandle === item.targetHandle
                        )
                      )
                  ),
                },
              },
            };
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setViewport: (state, action: PayloadAction<Viewport>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.viewport = action.payload;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setSaved: (state, action: PayloadAction<boolean>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.saved = action.payload;
        }

        if (action.payload) {
          tab.unstored = false;
          tab.new = false;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setSavedById: (state, action: PayloadAction<{ id: string | null; saved: boolean }>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === action.payload.id) {
          tab.saved = action.payload.saved;
        }

        if (action.payload.saved) {
          tab.unstored = false;
          tab.new = false;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setAllSaved: (state) => {
      state.list = state.list.map((tab) => {
        return { ...tab, saved: true };
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    updateEdgeVisibility: (state, action: PayloadAction<'execution' | 'tool' | 'data'>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          if (action.payload === 'execution') tab.show_execution = !tab.show_execution;
          if (action.payload === 'tool') tab.show_tools = !tab.show_tools;
          if (action.payload === 'data') tab.show_data = !tab.show_data;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    setServicePath: (state, action: PayloadAction<string>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.service_path = action.payload;
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    nodeVersionUpdate: (state, action: PayloadAction<{ nodeId: string; value: string }>) => {
      const { nodeId, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId)
              return {
                ...command,
                config: {
                  ...command.config,
                  version: value,
                },
              };
            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeJumpCopyState: (state, action: PayloadAction<{ source_node_id: string; target_node_id: string }>) => {
      const { source_node_id, target_node_id } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === source_node_id) {
              const sourceCommand = tab.commands.find((sourceCommand) => sourceCommand.node.id === target_node_id);
              const sourceCommandState = sourceCommand?.state;
              const sourceCommandFields = sourceCommand?.fields;

              if (sourceCommandState && sourceCommandFields) {
                return {
                  ...command,
                  state: {
                    ...sourceCommandState,
                    node_id: target_node_id,
                    max_jumps: {
                      type: NodeInputType.Number,
                      value: sourceCommandState?.state?.max_jumps?.value || '3',
                    },
                  },
                  fields: [
                    {
                      id: 'node_id',
                      tab: '1',
                      type: NodeFieldType.Option,
                      default_type: NodeInputType.Node,
                      label: 'Node',
                      description: 'Specify an existing node in the workflow to jump to and execute.',
                      required: true,
                    },
                    {
                      id: 'max_jumps',
                      tab: '1',
                      type: NodeFieldType.NumberExpression,
                      default_type: NodeInputType.Number,
                      minNumber: 1,
                      maxNumber: 50,
                      label: 'Max Jumps',
                      description:
                        'Specify the maximum number of times this node can jump. If reached, the "Max Jumps" execution anchor will be triggered. A node can only jump 50 times maximum.',
                      required: true,
                    },
                    ...sourceCommandFields,
                  ],
                };
              }
            }

            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    nodeWidthUpdate: (state, action: PayloadAction<{ nodeId: string; width: number }>) => {
      const { nodeId, width } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId) {
              return {
                ...command,
                node: {
                  ...command.node,
                  style: {
                    ...command.node.style,
                    width,
                  },
                  width,
                },
              };
            }

            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    /**
     * V2 Node Actions
     */
    nodeInputUpdate: (state, action: PayloadAction<NodeInputUpdate>) => {
      const { nodeId, fieldId, value } = action.payload;

      const removeErrors: string[] = [];

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            // the replacement is different depending on whether the field can be an expression
            const fieldName = action.payload?.fieldName;

            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: fieldName ? { ...command.state[fieldId], [fieldName]: value } : value,
                  },
                };
              }
            }

            return command;
          });

          tab.nodeErrors = tab.nodeErrors.map((nodeError) => {
            if (nodeError.id === nodeId) {
              const fieldName = action.payload?.fieldName;
              const newFields = nodeError?.fields?.filter(
                (nodeField) => nodeField.id !== fieldId && nodeField.id !== fieldName
              );
              if (newFields.length === 0) removeErrors.push(nodeError.id);

              return {
                ...nodeError,
                fields: newFields,
              };
            }

            return nodeError;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        return tab;
      });

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.nodeErrors = tab.nodeErrors.filter((nodeError) => !removeErrors.includes(nodeError.id));
        }
        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputArrayUpdate: (state, action: PayloadAction<NodeInputArrayUpdate>) => {
      const { nodeId, fieldId, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            const fieldName = action.payload?.fieldName;

            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: command.state[fieldId].map((item: any) => {
                      if (item.id === action.payload.nestedFieldId)
                        return {
                          ...item,
                          ...(fieldName ? { [fieldName]: value } : { value }),
                        };
                      return item;
                    }),
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);

          if (tab.run.savedParameters?.[action.payload?.nestedFieldId]) {
            delete tab.run.savedParameters[action.payload.nestedFieldId];
          }
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputArrayPush: (state, action: PayloadAction<NodeInputUpdate>) => {
      const { nodeId, fieldId, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: command.state[fieldId].concat(value),
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputArrayFilter: (state, action: PayloadAction<NodeInputArrayUpdate>) => {
      const { nodeId, fieldId, nestedFieldId } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: command.state[fieldId].filter(
                      (item: any) => item !== nestedFieldId && item.id !== nestedFieldId
                    ),
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        // delete any saved parameters
        if (tab.run.savedParameters?.[nestedFieldId]) {
          delete tab.run.savedParameters[nestedFieldId];
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputNestedUpdate: (state, action: PayloadAction<NodeInputNestedUpdate>) => {
      const { nodeId, fieldId, nestedFieldName, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            // the replacement is different depending on whether the field can be an expression
            const fieldName = action.payload?.fieldName;

            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: {
                      ...command.state[fieldId],
                      [nestedFieldName]: fieldName
                        ? { ...command.state[fieldId][nestedFieldName], [fieldName]: value }
                        : value,
                    },
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputNestedArrayUpdate: (state, action: PayloadAction<NodeInputNestedArrayUpdate>) => {
      const { nodeId, fieldId, nestedFieldName, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            // the replacement is different depending on whether the field can be an expression
            const fieldName = action.payload?.fieldName;

            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: {
                      ...command.state[fieldId],
                      [nestedFieldName]: command.state[fieldId][nestedFieldName].map((item: any) => {
                        if (item.id === action.payload.nestedFieldId)
                          return {
                            ...item,
                            ...(fieldName ? { [fieldName]: value } : { value }),
                          };
                        return item;
                      }),
                    },
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputNestedArrayPush: (state, action: PayloadAction<NodeInputNestedUpdate>) => {
      const { nodeId, fieldId, nestedFieldName, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: {
                      ...command.state[fieldId],
                      [nestedFieldName]: command.state[fieldId][nestedFieldName].concat(value),
                    },
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputNestedArrayFilter: (state, action: PayloadAction<NodeInputNestedArrayUpdate>) => {
      const { nodeId, fieldId, nestedFieldName, nestedFieldId } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: {
                      ...command.state[fieldId],
                      [nestedFieldName]: command.state[fieldId][nestedFieldName].filter(
                        (item: any) => item.id !== nestedFieldId
                      ),
                    },
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputArrayNestedArrayPush: (state, action: PayloadAction<NodeInputArrayNestedUpdate>) => {
      const { nodeId, fieldId, nestedFieldName, nestedFieldId, value } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: command.state[fieldId].map((item: any) => {
                      if (item.id === nestedFieldId)
                        return {
                          ...item,
                          [nestedFieldName]: item[nestedFieldName].concat(value),
                        };
                      return item;
                    }),
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputArrayNestedArrayFilter: (state, action: PayloadAction<NodeInputArrayNestedArrayUpdate>) => {
      const { nodeId, fieldId, nestedFieldName, nestedFieldId, childId } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: command.state[fieldId].map((item: any) => {
                      if (item.id === nestedFieldId)
                        return {
                          ...item,
                          [nestedFieldName]: item[nestedFieldName].filter(
                            (nestedItem: any) => nestedItem.id !== childId
                          ),
                        };
                      return item;
                    }),
                  },
                };
              }
            }

            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    nodeInputArrayNestedArrayUpdate: (state, action) => {
      const { nodeId, fieldId, nestedFieldName, nestedFieldId, childId, value, fieldName } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId || command?.state?.node_id === nodeId) {
              const fixedJumpFields = filterJumpFields(command);
              const fixedJumpFieldIds = fixedJumpFields.map((field) => field.id);
              const allowJumpUpdate = command?.state?.node_id === nodeId && !fixedJumpFieldIds.includes(fieldId);

              if (allowJumpUpdate || command.node.id === nodeId) {
                return {
                  ...command,
                  state: {
                    ...command.state,
                    [fieldId]: command.state[fieldId].map((item: any) => {
                      if (item.id === nestedFieldId) {
                        return {
                          ...item,
                          [nestedFieldName]: item[nestedFieldName].map((childItem: any) => {
                            if (childItem.id === childId) {
                              return {
                                ...childItem,
                                ...(fieldName ? { [fieldName]: value } : { value }),
                              };
                            }
                            return childItem;
                          }),
                        };
                      }
                      return item;
                    }),
                  },
                };
              }
            }
            return command;
          });

          tab.run.nodeErrors = tab.run.nodeErrors.filter((error) => error.node_id !== nodeId);
        }
        return tab;
      });

      state.active.saved = false;
    },
    refreshDynamicNodeData: (state, action: PayloadAction<NodeDataRefresh>) => {
      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id && !action.payload.moduleId) {
          tab.commands = tab.commands.map((command) => {
            if (action.payload.node_id === command.node.id) {
              return {
                ...command,
                output: {
                  ...command.output,
                  dynamic: action.payload.output,
                },
              };
            }

            return command;
          });

          tab.run.traces = tab.run.traces.map((trace) => {
            if (trace?.metadata?.node_id === action.payload.node_id && trace?.metadata?.workflow_id === tab.flow_id) {
              return {
                ...trace,
                metadata: {
                  ...trace.metadata,
                  output: action.payload.output,
                },
              };
            }

            return trace;
          });
        }

        if (tab.flow_id === state.active.flow_id && action.payload.moduleId) {
          tab.modules = tab.modules.map((mod) => {
            if (mod.flow?.flow_id === action.payload.moduleId) {
              mod.commands = mod.commands.map((command) => {
                if (action.payload.node_id === command.node.id) {
                  return {
                    ...command,
                    output: {
                      ...command.output,
                      dynamic: action.payload.output,
                    },
                  };
                }

                return command;
              });

              tab.run.traces = tab.run.traces.map((trace) => {
                if (
                  trace?.metadata?.node_id === action.payload.node_id &&
                  trace?.metadata?.workflow_id === action.payload.moduleId
                ) {
                  return {
                    ...trace,
                    metadata: {
                      ...trace.metadata,
                      output: action.payload.output,
                    },
                  };
                }

                return trace;
              });
            }

            return mod;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;
    },
    vMigrationAddFieldNested: (state, action: PayloadAction<any>) => {
      const { nodeId, fieldId, fieldName, fieldValue } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId)
              return {
                ...command,
                state: {
                  ...command.state,
                  [fieldId]: {
                    ...command.state[fieldId],
                    [fieldName]: fieldValue,
                  },
                },
              };

            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    vMigrationAddField: (state, action: PayloadAction<any>) => {
      const { nodeId, field, fieldValue } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId)
              return {
                ...command,
                fields: command.fields.concat(field),
                state: {
                  ...command.state,
                  [field.id]: fieldValue,
                },
              };

            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
    vMigrationRemoveField: (state, action: PayloadAction<any>) => {
      const { nodeId, fieldId } = action.payload;

      state.list = state.list.map((tab) => {
        if (tab.flow_id === state.active.flow_id) {
          tab.commands = tab.commands.map((command) => {
            if (command.node.id === nodeId)
              return {
                ...command,
                fields: command.fields.filter((field) => field.id !== fieldId),
              };

            return command;
          });
        }

        return tab;
      });

      // update the active tab
      const currentTab = state.list.find((tab) => tab.flow_id === state.active.flow_id);
      if (currentTab) state.active = currentTab;

      state.active.saved = false;
    },
  },
  extraReducers: (builder) => {
    const runReducer = runWorkflowThunk(builder);

    return {
      runReducer,
    };
  },
});

export type NodeDataRefresh = {
  node_id: string;
  output: any;
  moduleId?: string;
};

interface NodeInputUpdate {
  nodeId: string;
  fieldId: string;
  value?: any;
  fieldName?: string;
}

interface NodeInputArrayUpdate extends NodeInputUpdate {
  nestedFieldId: string;
}

interface NodeInputNestedUpdate extends NodeInputUpdate {
  nestedFieldName: string;
}

interface NodeInputArrayNestedUpdate extends NodeInputNestedUpdate {
  nestedFieldId: string;
}

interface NodeInputArrayNestedArrayUpdate extends NodeInputArrayNestedUpdate {
  childId: string;
}

interface NodeInputNestedArrayUpdate extends NodeInputNestedUpdate {
  nestedFieldId: string;
}

export const tabActions = tabSlice.actions;
export default tabSlice.reducer;
